import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { X, Plus, Loader2 } from 'lucide-react';

interface AddBusinessRuleFormProps {
  subcategory: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editing?: {
    id: string;
    rule_name: string;
    rule_text: string;
    severity: string;
    is_active: boolean;
  } | null;
}

const AddBusinessRuleForm: React.FC<AddBusinessRuleFormProps> = ({
  subcategory,
  isOpen,
  onClose,
  onSuccess,
  editing,
}) => {
  const [formData, setFormData] = useState({
    rule_name: '',
    rule_text: '',
    severity: 'Error',
    is_active: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editing) {
      setFormData({
        rule_name: editing.rule_name,
        rule_text: editing.rule_text,
        severity: editing.severity,
        is_active: editing.is_active,
      });
    } else {
      setFormData({ rule_name: '', rule_text: '', severity: 'Error', is_active: true });
    }
    setError(null);
  }, [editing, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!formData.rule_name.trim() || !formData.rule_text.trim()) {
      setError('Rule name and rule text are required.');
      return;
    }
    setIsSubmitting(true);
    try {
      if (editing) {
        await api.updateBusinessRule(editing.id, formData);
      } else {
        await api.createBusinessRule({ subcategory_name: subcategory, ...formData });
      }
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save business rule');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {editing ? 'Edit Business Rule' : 'Add Business Rule'}
            </h2>
            <Button variant="outline" size="sm" onClick={onClose} className="p-2">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <p className="text-xs text-gray-500 mb-4">
            Business rules are cross-field checks (e.g. "If Project Name = Internal, then Not Billable must be TRUE").
            They're applied to every row in addition to per-field validation.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rule Name *
              </label>
              <input
                type="text"
                name="rule_name"
                value={formData.rule_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder='e.g., Service Desk must be Not Billable'
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rule (plain English) *
              </label>
              <textarea
                name="rule_text"
                value={formData.rule_text}
                onChange={handleChange}
                required
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder='e.g., If "Project Name" contains "Service Desk" or "Issue Key" starts with "CSD-", then "Not Billable" MUST be TRUE. Flag the row with field "Not Billable".'
              />
              <p className="text-xs text-gray-500 mt-1">
                Tip: name the exact column to flag (e.g. <span className="font-mono">"Not Billable"</span>) so the error appears on the right field.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                <select
                  name="severity"
                  value={formData.severity}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Error">Error</option>
                  <option value="Warning">Warning</option>
                </select>
              </div>
              <div className="flex items-center pt-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex items-center gap-2">
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    {editing ? 'Save Changes' : 'Create Rule'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddBusinessRuleForm;
